import { backendClient } from "@/api/backendClient";
import {
  createPaginatedSupabaseQuery,
  createSearchSupabaseQuery,
  spawnGetApiFunction,
  spawnListInApiFunction,
  type ApiResourceConfig,
  type SearchOptionFilters,
} from "@/utils/api";

import type {
  DeviceDataTrigger,
  DeviceDataTriggerCurrentValue,
  DeviceDataTriggerEvaluation,
  DeviceDataTriggerInsert,
  DeviceDataTriggerUpdate,
  DeviceEventsTotal,
} from "@waku-care/types/contracts/internal";

import type { DeviceDataSeriesConfig } from "@/api/resources/deviceDataSeries/types";
import type { DatumTriggerTargetTable } from "@/supabase.types";
import type { QueryPaginationFilters } from "@/utils/pagination";

const table = "device_data_triggers";
const query = `
  *,
  series:device_data_series(*),
  deployment:deployments!inner(*),
  device:devices!inner(
    *,
    device_tags(*),
    manufacturer:manufacturers(*),
    model:device_models(*)
  )`;
export const apiConfigDeviceDataTriggers: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "series.metric_name", "device.name"],
  sortables: ["name", "series.metric_name", "device.name", "created_at", "updated_at"],
};

export const getDeviceDataTrigger = spawnGetApiFunction({ table, query });
export const getDeviceDataTriggersByIds = spawnListInApiFunction({ table, query });
// triggers are written by the backend, eg. a template trigger's copies on the devices of a model are kept in sync
export const deleteDeviceDataTrigger = async ({ match }: { match: { id: string } }) => {
  const { error } = await backendClient.delete<void>(`/device-data-triggers/${match.id}`);
  if (error) throw error;
};

// a new trigger has no series yet, it gets one once its calculation is chosen (see updateDeviceDataTrigger)
export const createDeviceDataTrigger = async ({ values }: { values: DeviceDataTriggerInsert }) => {
  const { data, error } = await backendClient.post<DeviceDataTrigger>(`/device-data-triggers`, { body: values });
  if (error) throw error;
  return data;
};

// the series is optional, eg. activating a trigger does not touch its calculation - the backend references the series
// of the calculation in the trigger's deployment
export const updateDeviceDataTrigger = async ({
  values,
  match,
  series,
}: {
  values: Omit<DeviceDataTriggerUpdate, "series">;
  match: { id: string };
  series?: { config: DeviceDataSeriesConfig };
}) => {
  const { data, error } = await backendClient.patch<DeviceDataTrigger>(`/device-data-triggers/${match.id}`, {
    body: { ...values, ...(series ? { series: series.config } : {}) },
  });
  if (error) throw error;
  return data;
};

export async function getPaginatedDeviceDataTriggers({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigDeviceDataTriggers.searchables,
      sortables: apiConfigDeviceDataTriggers.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });

  if (error) throw error;
  return { data, count };
}

export async function getDeviceDataTriggersBySearch({
  searchOptions,
  customFilters,
  customOrFilters,
  limit,
}: SearchOptionFilters) {
  const { data, count, error } = await createSearchSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigDeviceDataTriggers.searchables,
      sortables: apiConfigDeviceDataTriggers.sortables,
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });

  if (error) throw error;
  return { data, count };
}

// evaluates the trigger now, whether it is due or not
export const forceRunDeviceDataTrigger = async ({ id }: { id: string }) => {
  const { data, error } = await backendClient.post<DeviceDataTriggerEvaluation>(`/device-data-triggers/${id}/evaluate`);
  if (error) throw error;
  return data;
};

// the current value of the trigger's series, eg. to propose a new offset
export const getDeviceDataTriggerCurrentValue = async ({ id }: { id: string }) => {
  const { data, error } = await backendClient.get<DeviceDataTriggerCurrentValue>(
    `/device-data-triggers/${id}/current-value`,
  );
  if (error) throw error;
  return data;
};

// the count of all events of a device, and the time of the latest
export const countDeviceEventsTotal = async ({
  _device_id,
  _target_table,
}: {
  _device_id: string;
  // an event table, scalar values have no events
  _target_table: DatumTriggerTargetTable;
}) => {
  const { data, error } = await backendClient.get<DeviceEventsTotal>(`/devices/${_device_id}/events-total`, {
    params: { target_table: _target_table },
  });
  if (error) throw error;
  return data;
};
